<template>
  <section class="stages-section">
    <div class="section-container">
      <div class="section-label">المراحل الدراسية</div>
      <h2 class="section-title">اختر النظام الدراسي المناسب لك</h2>
      <p class="section-subtitle">
        محتوى احترافي يناسب جميع الأنظمة التعليمية التي أقدمها.
      </p>

      <div class="stages-grid">
        <article
          v-for="stage in stages"
          :key="stage.id"
          class="stage-card"
          :class="stage.accent"
        >
          <span class="stage-glow" aria-hidden="true"></span>
          <div class="stage-icon-wrap">
            <component :is="stage.icon" class="stage-icon" aria-hidden="true" />
          </div>
          <h3>{{ stage.title }}</h3>
          <p>{{ stage.description }}</p>
          <div class="stage-badges">
            <span v-for="badge in stage.badges" :key="badge">{{ badge }}</span>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup>
import { h } from 'vue';

const iconAttrs = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  'stroke-width': '1.7',
  'stroke-linecap': 'round',
  'stroke-linejoin': 'round',
};

const GraduationCapIcon = () =>
  h('svg', iconAttrs, [
    h('path', {
      d: 'M21.42 10.92a1 1 0 0 0-.02-1.84L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.83l8.57 3.9a2 2 0 0 0 1.66 0z',
    }),
    h('path', { d: 'M22 10v6' }),
    h('path', { d: 'M6 12.5V16a6 3 0 0 0 12 0v-3.5' }),
  ]);

const MosqueIcon = () =>
  h('svg', iconAttrs, [
    h('path', { d: 'M12 2v2.4' }),
    h('circle', { cx: '12', cy: '1.6', r: '0.6', fill: 'currentColor', stroke: 'none' }),
    h('path', { d: 'M9 7.6a3 3 0 0 1 6 0' }),
    h('path', {
      d: 'M5 21v-9a2 2 0 0 1 .7-1.5L9 7.6h6l3.3 2.9a2 2 0 0 1 .7 1.5v9',
    }),
    h('path', { d: 'M10 21v-4a2 2 0 1 1 4 0v4' }),
    h('path', { d: 'M3 21h18' }),
  ]);

const AwardIcon = () =>
  h('svg', iconAttrs, [
    h('circle', { cx: '12', cy: '8', r: '6' }),
    h('path', { d: 'M15.5 13.4 17 22l-5-3-5 3 1.5-8.6' }),
  ]);

const GlobeIcon = () =>
  h('svg', iconAttrs, [
    h('circle', { cx: '12', cy: '12', r: '9.5' }),
    h('path', { d: 'M12 2.5c-3.2 3-3.2 16 0 19' }),
    h('path', { d: 'M12 2.5c3.2 3 3.2 16 0 19' }),
    h('path', { d: 'M2.7 9h18.6' }),
    h('path', { d: 'M2.7 15h18.6' }),
  ]);

const stages = [
  {
    id: 'thanaweya',
    title: 'الثانوية العامة',
    description: 'شرح كامل لجميع سنوات الثانوية العامة',
    badges: ['أولى ثانوي', 'ثانية ثانوي', 'ثالثة ثانوي'],
    icon: GraduationCapIcon,
    accent: 'accent-blue',
  },
  {
    id: 'azhar',
    title: 'الثانوية الأزهرية',
    description: 'شرح مناهج الأزهر لجميع الصفوف الثانوية',
    badges: ['أولى أزهري', 'ثانية أزهري', 'ثالثة أزهري'],
    icon: MosqueIcon,
    accent: 'accent-emerald',
  },
  {
    id: 'bac',
    title: 'البكالوريا',
    description: 'إعداد شامل لنظام البكالوريا الجديد',
    badges: ['النظام الجديد'],
    icon: AwardIcon,
    accent: 'accent-purple',
  },
  {
    id: 'languages',
    title: 'ثانوي لغات',
    description: 'شرح ومتابعة لطلاب مدارس اللغات',
    badges: ['English Curriculum'],
    icon: GlobeIcon,
    accent: 'accent-teal',
  },
];
</script>

<style scoped>
*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.section-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}
.section-label {
  display: inline-block;
  background: rgba(5, 22, 41, 0.08);
  color: #051629;
  border: 1px solid rgba(5, 22, 41, 0.2);
  border-radius: 99px;
  padding: 0.3rem 1rem;
  font-size: 0.8rem;
  font-weight: 700;
  margin-bottom: 1rem;
}
.section-title {
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 800;
  color: #111827;
  line-height: 1.3;
}
.section-subtitle {
  color: #6b7280;
  font-size: 1rem;
  margin-top: 0.6rem;
  margin-bottom: 2.5rem;
  max-width: 560px;
  line-height: 1.7;
}

.stages-section {
  padding: 5rem 0;
  background: #fff;
}

.stages-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.75rem;
}
@media (min-width: 640px) {
  .stages-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.stage-card {
  position: relative;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  background: linear-gradient(160deg, #ffffff 0%, #fbfbfe 100%);
  border: 1px solid rgba(5, 22, 41, 0.08);
  border-radius: 24px;
  padding: 2.25rem 2rem;
  box-shadow: 0 10px 30px rgba(5, 22, 41, 0.06);
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.4s, border-color 0.4s;
}
.stage-card::before {
  content: '';
  position: absolute;
  inset: 0 0 auto 0;
  height: 4px;
  background: linear-gradient(90deg, var(--accent-1), var(--accent-2));
}
.stage-card:hover {
  transform: translateY(-10px);
  border-color: var(--accent-border);
  box-shadow: 0 26px 50px var(--glow), 0 10px 26px rgba(5, 22, 41, 0.08);
}

.stage-glow {
  position: absolute;
  top: -35%;
  inset-inline-end: -15%;
  width: 220px;
  height: 220px;
  background: radial-gradient(circle, var(--accent-1) 0%, transparent 70%);
  opacity: 0;
  filter: blur(10px);
  transition: opacity 0.4s;
  pointer-events: none;
}
.stage-card:hover .stage-glow {
  opacity: 0.16;
}

.stage-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(135deg, var(--accent-1), var(--accent-2));
  box-shadow: 0 10px 24px var(--glow);
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.stage-card:hover .stage-icon-wrap {
  transform: translateY(-4px) scale(1.06) rotate(-4deg);
}
.stage-icon {
  width: 30px;
  height: 30px;
}

.stage-card h3 {
  font-size: 1.25rem;
  font-weight: 800;
  color: #111827;
}
.stage-card > p {
  color: #6b7280;
  font-size: 0.92rem;
  line-height: 1.7;
}

.stage-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: auto;
  padding-top: 0.4rem;
}
.stage-badges span {
  background: var(--accent-soft);
  border: 1px solid var(--accent-border);
  color: var(--accent-1);
  border-radius: 99px;
  padding: 0.32rem 0.85rem;
  font-size: 0.78rem;
  font-weight: 700;
}

.accent-blue {
  --accent-1: #0066ff;
  --accent-2: #f7b424;
  --accent-soft: rgba(0, 102, 255, 0.1);
  --accent-border: rgba(0, 102, 255, 0.35);
  --glow: rgba(0, 102, 255, 0.35);
}
.accent-emerald {
  --accent-1: #059669;
  --accent-2: #f7b424;
  --accent-soft: rgba(5, 150, 105, 0.1);
  --accent-border: rgba(5, 150, 105, 0.35);
  --glow: rgba(5, 150, 105, 0.35);
}
.accent-purple {
  --accent-1: #7c3aed;
  --accent-2: #f7b424;
  --accent-soft: rgba(124, 58, 237, 0.1);
  --accent-border: rgba(124, 58, 237, 0.35);
  --glow: rgba(124, 58, 237, 0.35);
}
.accent-teal {
  --accent-1: #00d4a6;
  --accent-2: #f7b424;
  --accent-soft: rgba(0, 212, 166, 0.1);
  --accent-border: rgba(0, 212, 166, 0.35);
  --glow: rgba(0, 212, 166, 0.35);
}
</style>
